import Quagga, { QuaggaJSConfigObject } from '@ericblade/quagga2';
import { ReadonlyRefObject } from 'app/tools/hooks/local-storage/use-local-ref';
import _ from 'lodash';
import {
  calculateScanWindowOffsetPcts,
  getOffsets,
  getScanOffsetsFromTargetDims,
  getTargetDims,
  MatchedOffsets,
  WindowDimensions,
} from './scan-utils';

type GenerateQuaggaInitProps = {
  getQuaggaConfig: () => QuaggaJSConfigObject;
  windowDimsRef: React.MutableRefObject<WindowDimensions>;
  scanOffsetsRef: ReadonlyRefObject<MatchedOffsets>;
  setScanOffsetsRef: React.Dispatch<MatchedOffsets>;
  dimSwappedRef: ReadonlyRefObject<boolean>;
  setDimSwappedRef: React.Dispatch<boolean>;
  setLoading: React.Dispatch<React.SetStateAction<boolean>>;
  componentIsStillLoaded: () => boolean;
  onStreamError: (error: unknown) => void;
  handleCriticalError: React.Dispatch<
    React.SetStateAction<Error | null>
  >;
};

// Create Quagga initialization function
export default function initializeQuagga({
  getQuaggaConfig,
  windowDimsRef,
  scanOffsetsRef,
  setScanOffsetsRef,
  dimSwappedRef,
  setDimSwappedRef,
  setLoading,
  componentIsStillLoaded,
  onStreamError,
  handleCriticalError,
}: GenerateQuaggaInitProps): void {
  const initQuagga = () =>
    Quagga.init(getQuaggaConfig(), (error: unknown) => {
      // This callback runs once Quagga initialization finishes
      if (!componentIsStillLoaded()) return;

      if (error) {
        onStreamError?.(error);
        setLoading(false);
        return;
      }

      // As we are in async, get the current window dimensions from the
      // ref object set up above (it uses useEffect to stay updated)
      const currentWindowDims = _.clone(windowDimsRef.current);
      // Get the video feed current width and height (fallback to window size)
      const {
        width = currentWindowDims.width,
        height = currentWindowDims.height,
      } = Quagga.CameraAccess.getActiveTrack()?.getSettings() || {};

      // Calculate the positioning for the scan window to read barcodes in,
      // based on the current video feed dimensions
      const actualScanOffsets = calculateScanWindowOffsetPcts(
        currentWindowDims,
        { width, height }
      );

      // Check if the calculated positioning matches that which Quagga was
      // initialised with. If so, start Quagga...
      if (_.isEqual(getOffsets(scanOffsetsRef), actualScanOffsets)) {
        setScanOffsetsRef({
          ...actualScanOffsets,
          hasMatched: true,
        });

        setLoading(false);
        Quagga.start();
      } else {
        // ... if not, we either want to reinitialise Quagga with the
        // correct scan window positioning, or swap the target dimensions:

        // NOTE: on some devices, the width and height of the video feed
        // are swapped from what the dimensions requested. Check this:
        const foundSwapped = _.isEqual(
          { width: height, height: width },
          getTargetDims(dimSwappedRef.current)
        );

        // If dims have gone from swapped to not swapped, or visa
        // versa, re-init with the target dimensions in the new format.
        if (foundSwapped !== dimSwappedRef.current) {
          setScanOffsetsRef(
            getScanOffsetsFromTargetDims(
              currentWindowDims,
              dimSwappedRef.current
            )
          );
          setDimSwappedRef(foundSwapped);
        } else {
          // Otherwise keep the target dimensions, and re-init with
          // the correct offsets calculated above.
          setScanOffsetsRef({
            ...actualScanOffsets,
            hasMatched: false,
          });
        }

        // Stop Quagga, then reinitialise with the new config options
        Quagga.stop().then(initQuagga).catch(handleCriticalError);
      }
    });

  initQuagga();
}
